import { gql, TypedDocumentNode } from "@apollo/client";

import {
  ManagedUser,
  QueryUsersWithAccessToSpaceArgs,
  QueryUsersWithAccessToSpaceSuggestionsArgs,
  Role,
  SearchSuggestionsOutput,
  Space,
  SpaceAccessGroup,
  UserRoleBinding,
  UsersWithAccessToSpaceOutput,
  UserSpaceAccessRoleGrant,
} from "types/generated";

// grantedVia is the SpaceAccessGrantVia union — discriminate on __typename. SpaceAccessGroup carries
// the group; UserRoleBinding is the direct case (the granting space + propagated come from the grant).
type SpaceDetailsUserGrantedViaNode =
  | ({ __typename: "SpaceAccessGroup" } & Pick<SpaceAccessGroup, "id" | "groupName">)
  | ({ __typename: "UserRoleBinding" } & Pick<UserRoleBinding, "id">);

type SpaceDetailsUserGrantNode = Pick<UserSpaceAccessRoleGrant, "propagated"> & {
  role: Pick<Role, "id" | "name" | "isSystem">;
  space: Pick<Space, "id" | "name">;
  grantedVia: SpaceDetailsUserGrantedViaNode;
};

type SpaceDetailsUserNode = {
  user: Pick<ManagedUser, "id" | "username">;
  roles: Array<SpaceDetailsUserGrantNode>;
};

type UsersWithAccessToSpaceData = {
  usersWithAccessToSpace: {
    edges: Array<{ cursor: string; node: SpaceDetailsUserNode }>;
    pageInfo: UsersWithAccessToSpaceOutput["pageInfo"];
  };
};

export const USERS_WITH_ACCESS_TO_SPACE: TypedDocumentNode<
  UsersWithAccessToSpaceData,
  QueryUsersWithAccessToSpaceArgs
> = gql`
  query UsersWithAccessToSpace($space: ID!, $input: SearchInput!) {
    usersWithAccessToSpace(space: $space, input: $input) {
      edges {
        cursor
        node {
          user {
            id
            username
          }
          roles {
            propagated
            role {
              id
              name
              isSystem
            }
            space {
              id
              name
            }
            grantedVia {
              __typename
              ... on SpaceAccessGroup {
                id
                groupName
              }
              ... on UserRoleBinding {
                id
              }
            }
          }
        }
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        endCursor
        startCursor
      }
    }
  }
`;

type UsersWithAccessToSpaceCountData = {
  usersWithAccessToSpaceSuggestions: {
    filteredCount: SearchSuggestionsOutput["filteredCount"];
  };
};

export const USERS_WITH_ACCESS_TO_SPACE_COUNT: TypedDocumentNode<
  UsersWithAccessToSpaceCountData,
  QueryUsersWithAccessToSpaceSuggestionsArgs
> = gql`
  query UsersWithAccessToSpaceCount($space: ID!, $input: SearchSuggestionsInput!) {
    usersWithAccessToSpaceSuggestions(space: $space, input: $input) {
      filteredCount
    }
  }
`;
